import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { buttonVariants } from '@/components/ui/button'
import { WorkspaceApiPage } from '@/components/workspace-api/WorkspaceApiPage'
import { accountQuery, subaccountsQuery } from '@/lib/queries'
import { primarySubaccount } from '@/lib/stream-workspace'
import { prefetchWorkspaceUsage, workspaceApiQuery } from '@/lib/workspace-api-queries'

/**
 * The account workspace's API & MCP — its own space's (the primary sub-account's) endpoint and keys.
 * Gated on `delivery:api` like every product: not bought means no surface, redirect to Home.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/api')({
  loader: async ({ context, params }) => {
    const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
    if (!account.entitlements.includes(PRODUCT_ENTITLEMENTS.api)) {
      throw redirect({ to: '/app/accounts/$accountId', params: { accountId: params.accountId } })
    }
    const subs = await context.queryClient.ensureQueryData(subaccountsQuery(params.accountId))
    const subaccountId = primarySubaccount(subs.subaccounts).id
    const api = await context.queryClient.ensureQueryData(workspaceApiQuery(subaccountId))
    prefetchWorkspaceUsage(context.queryClient, subaccountId, api.endpoint)
  },
  component: AccountApiPage,
})

function AccountApiPage() {
  const { accountId } = Route.useParams()
  const { data: subs } = useSuspenseQuery(subaccountsQuery(accountId))
  return (
    <WorkspaceApiPage
      accountId={accountId}
      subaccountId={primarySubaccount(subs.subaccounts).id}
      referenceLink={
        <Link
          to="/app/accounts/$accountId/api/reference"
          params={{ accountId }}
          className={buttonVariants({ variant: 'outline', size: 'sm' })}
        >
          API reference
        </Link>
      }
    />
  )
}
